/**
 * Minimal .properties parser.
 *  - `key=value` or `key:value`
 *  - `#` or `!` comments
 *  - Backslash line continuation (line ends with `\`)
 *  - Escapes: \n \t \r \\ \=  \: \uXXXX
 *  - Keys use dots to nest (e.g. `game.bet=Bet` → { game: { bet: 'Bet' } })
 *  - If every child key of an object is a non-negative integer starting at 0,
 *    that branch is converted to an array (so `returnObjects: true` works).
 */
export type NestedDict = { [k: string]: string | NestedDict | unknown[] };

function setNested(root: NestedDict, parts: string[], value: string): void {
  let node: NestedDict = root;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i];
    if (typeof node[key] !== 'object' || node[key] === null) node[key] = {};
    node = node[key] as NestedDict;
  }
  node[parts[parts.length - 1]] = value;
}

function unescape(v: string): string {
  let out = '';
  for (let i = 0; i < v.length; i++) {
    const c = v[i];
    if (c === '\\' && i + 1 < v.length) {
      const next = v[i + 1];
      if (next === 'n') {
        out += '\n';
        i++;
        continue;
      }
      if (next === 't') {
        out += '\t';
        i++;
        continue;
      }
      if (next === 'r') {
        out += '\r';
        i++;
        continue;
      }
      if (next === 'u' && i + 5 < v.length) {
        const code = parseInt(v.slice(i + 2, i + 6), 16);
        if (!isNaN(code)) {
          out += String.fromCharCode(code);
          i += 5;
          continue;
        }
      }
      out += next;
      i++;
      continue;
    }
    out += c;
  }
  return out;
}

function arrayifyNumericBranches(node: unknown): unknown {
  if (node === null || typeof node !== 'object') return node;
  const obj = node as NestedDict;
  const keys = Object.keys(obj);
  const allNumeric = keys.length > 0 && keys.every(k => /^\d+$/.test(k));
  for (const k of keys) obj[k] = arrayifyNumericBranches(obj[k]) as never;
  if (allNumeric) {
    const arr: unknown[] = [];
    keys
      .map(k => parseInt(k, 10))
      .sort((a, b) => a - b)
      .forEach(i => arr.push(obj[String(i)]));
    return arr;
  }
  return obj;
}

export function parseProperties(text: string): NestedDict {
  const root: NestedDict = {};
  const rawLines = text.split(/\r?\n/);
  let buffer = '';
  for (let raw of rawLines) {
    if (buffer) {
      raw = buffer + raw.replace(/^\s+/, '');
      buffer = '';
    }
    const trimmed = raw.replace(/^\s+/, '');
    if (!trimmed || trimmed.startsWith('#') || trimmed.startsWith('!'))
      continue;
    // line continuation: ends with unescaped backslash
    if (/(^|[^\\])(\\\\)*\\$/.test(raw)) {
      buffer = raw.slice(0, -1);
      continue;
    }
    // find first un-escaped `=` or `:`
    let sep = -1;
    for (let i = 0; i < trimmed.length; i++) {
      const ch = trimmed[i];
      if (ch === '\\') {
        i++;
        continue;
      }
      if (ch === '=' || ch === ':') {
        sep = i;
        break;
      }
    }
    if (sep < 0) continue;
    const key = trimmed.slice(0, sep).trim();
    const value = trimmed.slice(sep + 1).replace(/^\s+/, '');
    if (!key) continue;
    setNested(root, key.split('.'), unescape(value));
  }
  return arrayifyNumericBranches(root) as NestedDict;
}
